import React, { useState, useEffect, useCallback } from 'react';
import { Spin, Empty, message } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-cn';

dayjs.extend(relativeTime);
dayjs.locale('zh-cn');

const GOLD = '#B8892A';
const BD = '#E8E0D0';
const BG = '#FAFAF8';
const MUTED = '#6B5E4E';
const MUTED_LIGHT = '#A8998A';

const CATEGORY_STRIP_COLORS = {
  bid: '#2B5CE6',
  order: '#2F9E44',
  finance: '#C8922A',
  account: '#6B7280',
};

const NOTIF_TYPE_CONFIG = {
  mandate_approved: { labelKey: 'notifications.mandateApproved', icon: '✅', color: '#1A4F8C', bg: 'rgba(26,79,140,0.08)', category: 'bid' },
  mandate_won:      { labelKey: 'notifications.mandateWon', icon: '🏆', color: '#2A6645', bg: 'rgba(42,102,69,0.08)', category: 'bid' },
  mandate_lost:     { labelKey: 'notifications.mandateLost', icon: '❌', color: '#9B2335', bg: 'rgba(155,35,53,0.08)', category: 'bid' },
  mandate_cancelled:{ labelKey: 'notifications.mandateCancelled', icon: '↩', color: MUTED_LIGHT, bg: 'rgba(168,153,138,0.08)', category: 'bid' },
  order_created:    { labelKey: 'notifications.orderCreated', icon: '🛍', color: '#1A4F8C', bg: 'rgba(26,79,140,0.08)', category: 'order' },
  order_paid:       { labelKey: 'notifications.orderPaid', icon: '💳', color: '#2A6645', bg: 'rgba(42,102,69,0.08)', category: 'order' },
  order_shipped:    { labelKey: 'notifications.orderShipped', icon: '📦', color: '#1A4F8C', bg: 'rgba(26,79,140,0.08)', category: 'order' },
  deposit_received: { labelKey: 'notifications.depositReceived', icon: '💴', color: '#2A6645', bg: 'rgba(42,102,69,0.08)', category: 'finance' },
  kyc_approved:     { labelKey: 'notifications.kycApproved', icon: '🎉', color: '#2A6645', bg: 'rgba(42,102,69,0.08)', category: 'account' },
  kyc_rejected:     { labelKey: 'notifications.kycRejected', icon: '⚠', color: '#9B2335', bg: 'rgba(155,35,53,0.08)', category: 'account' },
  system:           { labelKey: 'notifications.systemNotice', icon: '📢', color: MUTED_LIGHT, bg: 'rgba(168,153,138,0.08)', category: 'account' },
};

const CATEGORY_TABS = [
  { key: '', labelKey: 'common.all', icon: '📋' },
  { key: 'bid', labelKey: 'notifications.catBid', icon: '🔨', types: ['mandate_approved', 'mandate_won', 'mandate_lost', 'mandate_cancelled'] },
  { key: 'order', labelKey: 'notifications.catOrder', icon: '📦', types: ['order_created', 'order_paid', 'order_shipped'] },
  { key: 'finance', labelKey: 'notifications.catFinance', icon: '💴', types: ['deposit_received'] },
  { key: 'account', labelKey: 'notifications.catAccount', icon: '👤', types: ['kyc_approved', 'kyc_rejected', 'system'] },
];

function getNotifConfig(type) {
  return NOTIF_TYPE_CONFIG[type] || NOTIF_TYPE_CONFIG.system;
}

function getStripColor(type) {
  const cfg = getNotifConfig(type);
  return CATEGORY_STRIP_COLORS[cfg.category] || CATEGORY_STRIP_COLORS.account;
}

function getActionButtons(type) {
  switch (type) {
    case 'mandate_won':
      return [
        { label: '立即付款', style: 'gold' },
        { label: '查看订单', style: 'secondary' },
        { label: '联系客服', style: 'secondary' },
      ];
    case 'mandate_approved':
      return [
        { label: '查看委托', style: 'primary' },
        { label: '修改上限', style: 'secondary' },
      ];
    case 'mandate_lost':
      return [
        { label: '重新委托', style: 'gold' },
        { label: '浏览同类拍品', style: 'secondary' },
      ];
    case 'order_created':
      return [
        { label: '立即付款', style: 'gold' },
        { label: '查看订单', style: 'secondary' },
      ];
    case 'order_paid':
      return [
        { label: '查看订单', style: 'primary' },
      ];
    case 'order_shipped':
      return [
        { label: '追踪物流', style: 'primary' },
        { label: '查看检品报告', style: 'secondary' },
      ];
    case 'deposit_received':
      return [
        { label: '查看账单', style: 'primary' },
        { label: '去委托竞拍', style: 'gold' },
      ];
    case 'system':
      return [
        { label: '联系客服', style: 'secondary' },
        { label: '我知道了', style: 'primary' },
      ];
    default:
      return [
        { label: '查看详情', style: 'primary' },
      ];
  }
}

const actionButtonStyles = {
  primary: {
    background: '#110E0A', border: '1px solid #110E0A', color: '#FDFBF6',
    fontFamily: 'var(--serif)', fontSize: 14, letterSpacing: 1,
    padding: '9px 20px', borderRadius: 2, cursor: 'pointer',
    transition: 'all 0.18s',
  },
  secondary: {
    background: 'transparent', border: '1px solid ' + BD, color: MUTED,
    fontFamily: 'var(--serif)', fontSize: 14, letterSpacing: 1,
    padding: '9px 20px', borderRadius: 2, cursor: 'pointer',
    transition: 'all 0.18s',
  },
  gold: {
    background: GOLD, border: '1px solid ' + GOLD, color: '#fff',
    fontFamily: 'var(--serif)', fontSize: 14, letterSpacing: 1,
    padding: '9px 20px', borderRadius: 2, cursor: 'pointer',
    transition: 'all 0.18s',
  },
};

export default function Notifications() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  // 从铃铛点过来时带 state.focusId → 自动选中那条
  const focusId = location.state?.focusId || null;
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [activeCategory, setActiveCategory] = useState('');
  const [selectedId, setSelectedId] = useState(focusId);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/notifications');
      setNotifications(data.data || []);
    } catch (err) {
      message.error(t('common.failed'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  // 监听铃铛轮询更新事件 → 自动刷新列表
  // （解决用户停留在消息中心时新消息不显示的问题）
  useEffect(() => {
    const handler = () => fetchNotifications();
    window.addEventListener('notifications:updated', handler);
    return () => window.removeEventListener('notifications:updated', handler);
  }, [fetchNotifications]);

  // 跟随 location.state.focusId 变化（多次从铃铛跳过来时同步）
  // 同时立即刷新一次，确保新消息已在列表中
  useEffect(() => {
    if (focusId) {
      setSelectedId(focusId);
      fetchNotifications();
    }
  }, [focusId, fetchNotifications]);

  const handleMarkRead = async (notif) => {
    setSelectedId(notif.id);
    if (!notif.is_read) {
      try {
        await api.put(`/notifications/${notif.id}/read`);
        setNotifications((prev) => prev.map((n) => n.id === notif.id ? { ...n, is_read: true } : n));
        // 通知铃铛刷新未读数
        window.dispatchEvent(new Event('notifications:updated'));
      } catch (_) {}
    }
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      await api.put('/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      window.dispatchEvent(new Event('notifications:updated'));
      message.success(t('notifications.markAllRead'));
    } catch (err) {
      message.error(t('common.failed'));
    } finally {
      setMarkingAll(false);
    }
  };

  const handleDelete = async (notifId) => {
    try {
      await api.delete(`/notifications/${notifId}`);
      setNotifications((prev) => prev.filter((n) => n.id !== notifId));
      if (selectedId === notifId) setSelectedId(null);
      window.dispatchEvent(new Event('notifications:updated'));
      message.success(t('notifications.delete'));
    } catch (_) {
      message.error(t('common.failed'));
    }
  };

  const catFilter = CATEGORY_TABS.find((c) => c.key === activeCategory);
  let filteredNotifs = activeCategory && catFilter?.types
    ? notifications.filter((n) => catFilter.types.includes(n.type))
    : notifications;

  // Apply search filter
  if (searchQuery.trim()) {
    const q = searchQuery.trim().toLowerCase();
    filteredNotifs = filteredNotifs.filter((n) =>
      (n.title || '').toLowerCase().includes(q) ||
      (n.body || n.content || '').toLowerCase().includes(q)
    );
  }

  const unreadCount = notifications.filter((n) => !n.is_read).length;
  const selectedNotif = notifications.find((n) => n.id === selectedId);

  return (
    <div style={{ background: BG, minHeight: 'calc(100vh - 120px)' }}>
      {/* Page header */}
      <div style={{ background: '#fff', borderBottom: '1px solid ' + BD, padding: isMobile ? '14px 16px' : '24px 32px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 3, color: GOLD, marginBottom: 8 }}>MESSAGE CENTER</div>
            <h1 style={{ fontFamily: 'var(--serif)', fontSize: 26, fontWeight: 600, color: '#110E0A', letterSpacing: 2, margin: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
              {t('notifications.title')}
              {unreadCount > 0 && (
                <span style={{ background: '#9B2335', color: '#fff', fontFamily: 'var(--mono)', fontSize: 11, padding: '2px 8px', borderRadius: 10 }}>{unreadCount}</span>
              )}
            </h1>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              disabled={markingAll}
              style={{ padding: '8px 16px', background: 'transparent', border: '1px solid ' + BD, borderRadius: 3, cursor: 'pointer', fontFamily: 'var(--sans)', fontSize: 12, color: MUTED, transition: 'all 0.15s' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = BD; e.currentTarget.style.color = MUTED; }}
            >
              {markingAll ? t('common.loading') : t('notifications.markAllRead')}
            </button>
          )}
        </div>
      </div>

      {isMobile && (
        <div style={{ background: '#fff', borderBottom: '1px solid ' + BD, display: 'flex', overflowX: 'auto', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
          {CATEGORY_TABS.map((cat) => {
            const catCount = cat.types ? notifications.filter((n) => !n.is_read && cat.types.includes(n.type)).length : unreadCount;
            const isActive = activeCategory === cat.key;
            return (
              <button key={cat.key} onClick={() => setActiveCategory(cat.key)} style={{
                flexShrink: 0, padding: '10px 14px', background: 'transparent', border: 'none',
                borderBottom: `2px solid ${isActive ? GOLD : 'transparent'}`,
                cursor: 'pointer', fontSize: 13, color: isActive ? GOLD : MUTED,
                fontFamily: 'var(--sans)', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5,
              }}>
                {cat.icon} {cat.label}
                {catCount > 0 && <span style={{ fontFamily: 'var(--mono)', fontSize: 9, padding: '1px 5px', borderRadius: 8, background: '#9B2335', color: '#fff' }}>{catCount}</span>}
              </button>
            );
          })}
        </div>
      )}
      <div style={isMobile ? { maxWidth: 1100, margin: '0 auto' } : { maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: '200px 1fr 360px', height: 'calc(100vh - 185px)', overflow: 'hidden' }}>
        {/* Left sidebar: categories */}
        {!isMobile && <div style={{ background: '#FDFBF6', borderRight: '1px solid ' + BD, overflowY: 'auto', padding: '16px 0', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 8, letterSpacing: '2.5px', color: MUTED_LIGHT, padding: '0 16px', marginBottom: 12, textTransform: 'uppercase' }}>
            {t('notifications.title')}
          </div>
          {CATEGORY_TABS.map((cat) => {
            const catCount = cat.types
              ? notifications.filter((n) => !n.is_read && cat.types.includes(n.type)).length
              : unreadCount;
            const isActive = activeCategory === cat.key;
            return (
              <div
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 16px', cursor: 'pointer',
                  borderLeft: '3px solid ' + (isActive ? GOLD : 'transparent'),
                  background: isActive ? 'rgba(184,137,42,0.06)' : 'transparent',
                  transition: 'all 0.15s',
                }}
              >
                <span style={{ fontSize: 15 }}>{cat.icon}</span>
                <span style={{ flex: 1, fontSize: 13, color: isActive ? '#110E0A' : MUTED, fontWeight: isActive ? 500 : 400 }}>{cat.label}</span>
                {catCount > 0 && (
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 9, padding: '1px 5px', borderRadius: 8, background: '#9B2335', color: '#fff', minWidth: 16, textAlign: 'center' }}>{catCount}</span>
                )}
              </div>
            );
          })}

          {/* Divider and settings link */}
          <div style={{ height: 1, background: BD, margin: '10px 16px' }} />
          <button
            onClick={handleMarkAllRead}
            disabled={markingAll}
            style={{
              margin: '0 16px', padding: '8px 12px',
              border: '1px solid ' + BD, borderRadius: 2,
              fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 1,
              color: MUTED_LIGHT, cursor: 'pointer', textAlign: 'center',
              background: 'transparent', transition: 'all 0.14s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = BD; e.currentTarget.style.color = MUTED_LIGHT; }}
          >
            {markingAll ? t('common.loading') : '✓ ' + t('notifications.markAllRead')}
          </button>
          <button
            onClick={() => navigate('/settings')}
            style={{
              margin: '6px 16px 0', padding: '8px 12px',
              border: '1px solid ' + BD, borderRadius: 2,
              fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 1,
              color: MUTED_LIGHT, cursor: 'pointer', textAlign: 'center',
              background: 'transparent', transition: 'all 0.14s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = BD; e.currentTarget.style.color = MUTED_LIGHT; }}
          >
            ⚙ {t('settings.notification')}
          </button>
        </div>}

        {/* Middle: notification list */}
        <div style={isMobile ? { display: selectedId && isMobile ? 'none' : 'flex', flexDirection: 'column' } : { borderRight: '1px solid ' + BD, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          {/* Search bar */}
          <div style={{ padding: '8px 12px', borderBottom: '1px solid ' + BD, flexShrink: 0 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              border: '1px solid ' + BD, borderRadius: 2,
              background: '#FDFBF6', padding: '6px 10px',
              transition: 'border 0.15s',
            }}>
              <span style={{ fontSize: 12, color: MUTED_LIGHT }}>🔍</span>
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('common.search')}
                style={{
                  flex: 1, border: 'none', outline: 'none', background: 'transparent',
                  fontFamily: 'var(--sans)', fontSize: 12, color: '#110E0A',
                }}
              />
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            <Spin spinning={loading}>
              {filteredNotifs.length === 0 && !loading ? (
                <Empty description={t('notifications.empty')} style={{ padding: '60px 0' }} />
              ) : (
                filteredNotifs.map((notif) => {
                  const cfg = getNotifConfig(notif.type);
                  const stripColor = getStripColor(notif.type);
                  const isSelected = selectedId === notif.id;
                  return (
                    <div
                      key={notif.id}
                      onClick={() => handleMarkRead(notif)}
                      style={{
                        display: 'flex', cursor: 'pointer',
                        background: isSelected ? '#F4F1EC' : notif.is_read ? '#fff' : 'rgba(184,137,42,0.03)',
                        borderBottom: '1px solid ' + BD,
                        transition: 'all 0.14s',
                      }}
                      onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#FAF8F5'; }}
                      onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = notif.is_read ? '#fff' : 'rgba(184,137,42,0.03)'; }}
                    >
                      {/* Colored left strip */}
                      <div style={{
                        width: 3, flexShrink: 0, alignSelf: 'stretch',
                        background: stripColor, borderRadius: 2,
                      }} />
                      <div style={{ flex: 1, padding: '12px 14px' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                              <span style={{ fontFamily: 'var(--mono)', fontSize: 9, padding: '1px 6px', borderRadius: 2, background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                              {!notif.is_read && (
                                <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#9B2335', display: 'inline-block', flexShrink: 0 }} />
                              )}
                              <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: MUTED_LIGHT, marginLeft: 'auto' }}>
                                {notif.created_at ? dayjs(notif.created_at).format('MM-DD HH:mm') : ''}
                              </span>
                            </div>
                            <div style={{ fontSize: 13, fontWeight: notif.is_read ? 400 : 500, color: '#110E0A', marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.4 }}>
                              {notif.title}
                            </div>
                            <div style={{ fontSize: 11, color: MUTED_LIGHT, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.5 }}>
                              {notif.body || notif.content || '—'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </Spin>
          </div>
        </div>

        {/* Right: detail */}
        {(!isMobile || selectedId) && <div style={isMobile ? { position: 'fixed', inset: 0, zIndex: 300, background: '#fff', overflowY: 'auto', display: 'flex', flexDirection: 'column' } : { overflowY: 'auto', background: '#fff', display: 'flex', flexDirection: 'column' }}>
          {isMobile && selectedId && (
            <div style={{ padding: '12px 16px', borderBottom: '1px solid ' + BD, display: 'flex', alignItems: 'center', gap: 10 }}>
              <button onClick={() => setSelectedId(null)} style={{ background: 'transparent', border: '1px solid ' + BD, borderRadius: 3, padding: '6px 12px', cursor: 'pointer', fontSize: 12, color: MUTED, fontFamily: 'var(--sans)' }}>← 返回</button>
            </div>
          )}
          {selectedNotif ? (
            <>
              {(() => {
                const cfg = getNotifConfig(selectedNotif.type);
                const stripColor = getStripColor(selectedNotif.type);
                const actions = getActionButtons(selectedNotif.type);
                return (
                  <>
                    {/* Detail header */}
                    <div style={{ padding: '16px 24px', borderBottom: '1px solid ' + BD, flexShrink: 0, position: 'relative' }}>
                      {/* Category + time row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 1.5, marginBottom: 8 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: stripColor }} />
                        <span style={{ color: MUTED_LIGHT }}>{cfg.label}</span>
                        <span style={{ color: BD }}>·</span>
                        <span style={{ color: MUTED_LIGHT }}>
                          {selectedNotif.created_at ? dayjs(selectedNotif.created_at).format('YYYY-MM-DD HH:mm:ss') : ''}
                        </span>
                      </div>

                      {/* Title */}
                      <div style={{ fontFamily: 'var(--serif)', fontSize: 18, fontWeight: 600, color: '#110E0A', lineHeight: 1.4, marginBottom: 8, paddingRight: 80 }}>
                        {selectedNotif.title}
                      </div>

                      {/* Meta */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontFamily: 'var(--mono)', fontSize: 9, color: MUTED_LIGHT }}>
                        <span>ID: #{selectedNotif.id}</span>
                        <span>{selectedNotif.is_read ? '✓ ' + t('notifications.markRead') : '● ' + t('notifications.unread')}</span>
                      </div>

                      {/* Header action buttons (top right) */}
                      <div style={{ position: 'absolute', top: 16, right: 24, display: 'flex', gap: 6 }}>
                        <button
                          onClick={() => handleDelete(selectedNotif.id)}
                          style={{
                            padding: '5px 10px', border: '1px solid ' + BD, borderRadius: 1,
                            fontFamily: 'var(--mono)', fontSize: 8, color: MUTED_LIGHT,
                            cursor: 'pointer', transition: 'all 0.12s', background: 'transparent',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#9B2335'; e.currentTarget.style.color = '#9B2335'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = BD; e.currentTarget.style.color = MUTED_LIGHT; }}
                        >🗑 {t('notifications.delete')}</button>
                      </div>
                    </div>

                    {/* Detail body */}
                    <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
                      {/* Body content card */}
                      <div style={{
                        background: '#FAF6EE', border: '1px solid ' + BD, borderRadius: 2,
                        padding: 28, position: 'relative',
                        boxShadow: '0 2px 12px rgba(26,18,8,0.10)',
                      }}>
                        <div style={{ fontSize: 14, color: MUTED, lineHeight: 2, position: 'relative', zIndex: 1 }}>
                          {selectedNotif.body || selectedNotif.content || t('common.noData')}
                        </div>
                      </div>

                      {/* Time info */}
                      <div style={{ marginTop: 20, padding: '12px 14px', background: BG, border: '1px solid ' + BD, borderRadius: 3 }}>
                        <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: MUTED_LIGHT }}>
                          {selectedNotif.created_at ? dayjs(selectedNotif.created_at).fromNow() : ''}
                        </div>
                      </div>
                    </div>

                    {/* Action buttons bar */}
                    {actions.length > 0 && (
                      <div style={{ padding: '14px 24px', borderTop: '1px solid ' + BD, display: 'flex', gap: 10, flexShrink: 0, background: '#FDFBF6' }}>
                        {actions.map((action, idx) => (
                          <button
                            key={idx}
                            onClick={() => message.info(`操作：${action.label}`)}
                            style={actionButtonStyles[action.style] || actionButtonStyles.secondary}
                          >
                            {action.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '40px 24px' }}>
              <div style={{ fontSize: 48, opacity: 0.2, marginBottom: 12 }}>💬</div>
              <div style={{ fontFamily: 'var(--serif)', fontSize: 16, color: MUTED, textAlign: 'center' }}>{t('notifications.empty')}</div>
            </div>
          )}
        </div>}
      </div>
    </div>
  );
}
